import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSystemStatusContext } from '../contexts/SystemStatusContext';
import type { BetaWelcomeContent } from '../types/beta-welcome';
import { DEFAULT_BETA_WELCOME_CONTENT } from '../types/beta-welcome';
import BetaWelcomeContentView from './BetaWelcomeContentView';
import '../styles/system-status.css';

const STORAGE_KEY = 'wt_beta_welcome_seen';

function openBetaFeedback() {
  window.dispatchEvent(new CustomEvent('wt-open-beta-feedback'));
}

interface BetaWelcomeModalProps {
  forceOpen?: boolean;
  previewContent?: BetaWelcomeContent | null;
  previewKnownIssues?: string[];
  previewMode?: boolean;
  onClose?: () => void;
}

export function isBetaExperienceActive(status: { appEnv: string; beta: { mode: string } } | null): boolean {
  if (!status) return false;
  return status.appEnv === 'preprod' || status.beta.mode !== 'open';
}

export function shouldShowBetaUI(
  status: { appEnv: string; beta: { mode: string } } | null,
  isAdmin = false,
): boolean {
  if (isAdmin) return true;
  return isBetaExperienceActive(status);
}

export function hasSeenBetaWelcome(): boolean {
  return localStorage.getItem(STORAGE_KEY) === '1';
}

export function markBetaWelcomeSeen(): void {
  localStorage.setItem(STORAGE_KEY, '1');
}

export default function BetaWelcomeModal({
  forceOpen = false,
  previewContent = null,
  previewKnownIssues,
  previewMode = false,
  onClose,
}: BetaWelcomeModalProps) {
  const { status } = useSystemStatusContext();
  const [dismissed, setDismissed] = useState(() => !previewMode && hasSeenBetaWelcome());

  const shouldShow = previewMode
    ? forceOpen
    : !dismissed && (forceOpen || (isBetaExperienceActive(status) && !hasSeenBetaWelcome()));

  const handleClose = useCallback(() => {
    if (!previewMode) {
      markBetaWelcomeSeen();
      setDismissed(true);
    }
    onClose?.();
  }, [onClose, previewMode]);

  useEffect(() => {
    if (!shouldShow) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shouldShow, handleClose]);

  if (!shouldShow) return null;

  const content = previewContent
    ?? status?.beta.welcomeContent
    ?? DEFAULT_BETA_WELCOME_CONTENT;
  const knownIssues = previewKnownIssues ?? status?.beta.knownIssues ?? [];

  return createPortal(
    <div
      className="beta-feedback-overlay beta-welcome-overlay"
      role="presentation"
      onClick={previewMode ? undefined : handleClose}
    >
      <div
        className="beta-welcome-modal"
        role="dialog"
        aria-labelledby="beta-welcome-title"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        <BetaWelcomeContentView
          content={content}
          knownIssues={knownIssues}
          onClose={handleClose}
          onOpenFeedback={() => {
            openBetaFeedback();
            handleClose();
          }}
          previewMode={previewMode}
        />
      </div>
    </div>,
    document.body,
  );
}
